import type { ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

export const standardActionVariants = cva(
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 text-sm font-extrabold transition-[background-color,border-color,color,transform,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--standard-steel,#2f5d73)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]',
  {
    variants: {
      variant: {
        primary: 'border border-[var(--standard-steel,#2f5d73)] bg-[var(--standard-steel,#2f5d73)] !text-white [-webkit-text-fill-color:#ffffff] shadow-[0_8px_18px_rgba(47,93,115,0.16)] hover:bg-[#264f62]',
        secondary: 'border border-[var(--standard-line-strong,#bcc5ca)] bg-[var(--standard-paper,#fcfbf8)] text-[var(--standard-ink,#17151a)] hover:border-[var(--standard-steel,#2f5d73)] hover:bg-[var(--standard-steel-soft,#e4edf1)]',
        quiet: 'border border-transparent bg-transparent text-[var(--standard-steel,#2f5d73)] hover:bg-[var(--standard-steel-soft,#e4edf1)]',
        earned: 'border border-[var(--standard-bronze,#b28a49)] bg-[var(--standard-bronze,#b28a49)] text-[#171715] shadow-[0_8px_18px_rgba(178,138,73,0.16)] hover:bg-[#a77d38]',
        destructive: 'border border-[var(--standard-danger,#8f4d44)] bg-transparent text-[var(--standard-danger,#8f4d44)] hover:bg-[#fbf3f2]',
      },
      size: {
        default: 'min-h-11 px-4',
        compact: 'min-h-11 px-3 text-xs',
        large: 'min-h-12 px-6 text-base',
        icon: 'size-11 min-h-11 px-0',
      },
      fullWidth: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'default', fullWidth: false },
  },
);

export interface StandardActionProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof standardActionVariants> {
  loading?: boolean;
  loadingLabel?: string;
}

export function StandardAction({
  variant,
  size,
  fullWidth,
  loading = false,
  loadingLabel = 'Working…',
  disabled,
  className,
  children,
  ...props
}: StandardActionProps) {
  return (
    <button
      type="button"
      data-standard-action={variant ?? 'primary'}
      className={cn(standardActionVariants({ variant, size, fullWidth }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
      {loading ? loadingLabel : children}
    </button>
  );
}
